import { Stack } from '@mui/material';
import CaptionCard from '@shared/components/CaptionCard';
import SelectField, { Identifiable } from '@shared/components/SelectField';
import TextField from '@shared/components/TextField';
import { type Control } from 'react-hook-form';
import { useIntl } from 'react-intl';
import PhonePrefixAutocomplete from '../../../../shared/components/phone-prefix-autocomplete';
import { EditClient } from '../../entity';
import { getClientFormFields } from './client-form.hooks';
import { PhoneDuplicateValidator } from '@features/client/validators';

type ClientFormProps = {
  control: Control<EditClient>;
  serviceOptions?: Identifiable[];
  isExpertPlan: boolean;
  isRegularClient: boolean;
  handleInputRef?: (fieldPath: keyof EditClient) => (el: HTMLInputElement | null) => void;
  validatePhoneUnique?: PhoneDuplicateValidator;
};

const ClientForm = (props: ClientFormProps) => {
  const intl = useIntl();
  const {
    control,
    serviceOptions,
    isExpertPlan,
    isRegularClient,
    handleInputRef,
    validatePhoneUnique,
  } = props;

  const formFieldsConfig = getClientFormFields(
    intl,
    serviceOptions,
    isExpertPlan,
    isRegularClient,
    control,
    validatePhoneUnique,
  );
  return (
    <Stack spacing={2} padding={1}>
      {formFieldsConfig.map(field => {
        if (!field.shouldDisplay) {
          return null;
        }

        switch (field.type) {
          case 'text':
            return (
              <TextField
                key={field.fieldPath}
                fieldPath={field.fieldPath}
                control={control}
                label={field.label}
                type={field.type}
                fullWidth
                helperText={field.helperText}
                required={field.required}
                rules={field.rules}
                multiline={field.multiline}
                minRows={field.minRows}
                maxRows={field.maxRows}
                inputRef={(el: HTMLInputElement) =>
                  handleInputRef ? handleInputRef(field.fieldPath)(el) : undefined
                }
              />
            );
          case 'tel':
            return (
              <Stack key={field.fieldPath} direction="row" spacing={1} alignItems="flex-start">
                <PhonePrefixAutocomplete control={control} name="phonePrefix" />
                <TextField
                  fieldPath={field.fieldPath}
                  control={control}
                  label={field.label}
                  type={field.type}
                  fullWidth
                  helperText={field.helperText}
                  required={field.required}
                  rules={field.rules}
                  inputRef={(el: HTMLInputElement) =>
                    handleInputRef ? handleInputRef(field.fieldPath)(el) : undefined
                  }
                />
              </Stack>
            );
          case 'phonePrefix':
            return null;
          case 'select':
            return (
              <SelectField
                key={field.fieldPath}
                keyExtractor={field.keyExtractor!}
                labelExtractor={field.labelExtractor!}
                fieldPath={field.fieldPath}
                label={field.label}
                multiple={field.multiple}
                control={control}
                size="small"
                items={field.items!}
                inputRef={handleInputRef ? el => handleInputRef(field.fieldPath)(el) : undefined}
              />
            );
          case 'captionCard':
            return (
              <CaptionCard
                key={field.fieldPath}
                title={field.captionTitle!}
                caption={field.caption!}
                endElement={field.endElement}
              />
            );
          default:
            return null;
        }
      })}
    </Stack>
  );
};
export default ClientForm;
